const AUTH_KEYS = ['accessToken', 'refreshToken', 'userID', 'userRole', 'tenantID', 'isBusinessVerified', 'subscriptionStatus'];
const AUTH_META_KEYS = ['onboardingStep', 'isOnboardingComplete', 'isBusinessVerified', 'isEmailVerified', 'tourSeen'];

const hasWindow = () => typeof window !== 'undefined';

const getLocalStorage = () => (hasWindow() ? window.localStorage : null);
const getSessionStorage = () => (hasWindow() ? window.sessionStorage : null);

const getActiveStorage = () => {
  const localStorageRef = getLocalStorage();
  const sessionStorageRef = getSessionStorage();

  if (!localStorageRef || !sessionStorageRef) return null;

  const localHasAuth = AUTH_KEYS.some((key) => Boolean(localStorageRef.getItem(key)));
  if (localHasAuth) return localStorageRef;

  const sessionHasAuth = AUTH_KEYS.some((key) => Boolean(sessionStorageRef.getItem(key)));
  if (sessionHasAuth) return sessionStorageRef;

  return localStorageRef;
};

export const getStoredAuthValue = (key) => {
  const localStorageRef = getLocalStorage();
  const sessionStorageRef = getSessionStorage();
  if (!localStorageRef || !sessionStorageRef) return '';
  return localStorageRef.getItem(key) || sessionStorageRef.getItem(key) || '';
};

export const persistAuthSession = ({ data, rememberDevice }) => {
  if (!hasWindow() || !data?.tokens || !data?.user) return;

  const localStorageRef = getLocalStorage();
  const sessionStorageRef = getSessionStorage();
  const targetStorage = rememberDevice ? localStorageRef : sessionStorageRef;
  const otherStorage = rememberDevice ? sessionStorageRef : localStorageRef;

  targetStorage.setItem('accessToken', data.tokens.access.token);
  targetStorage.setItem('refreshToken', data.tokens.refresh.token);
  targetStorage.setItem('userID', data.user.id);
  targetStorage.setItem('userRole', data.user.role);

  if (data.user.tenantId) {
    targetStorage.setItem('tenantID', data.user.tenantId);
  } else {
    targetStorage.removeItem('tenantID');
  }

  AUTH_META_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(data.user, key)) {
      targetStorage.setItem(key, String(data.user[key] ?? ''));
    } else {
      targetStorage.removeItem(key);
    }
  });

  AUTH_KEYS.forEach((key) => {
    otherStorage.removeItem(key);
  });
  AUTH_META_KEYS.forEach((key) => {
    otherStorage.removeItem(key);
  });
};

export const updateStoredAuthTokens = ({ accessToken, refreshToken }) => {
  if (!hasWindow()) return;

  const localStorageRef = getLocalStorage();
  const sessionStorageRef = getSessionStorage();
  const targetStorage = localStorageRef.getItem('refreshToken') ? localStorageRef : sessionStorageRef;
  const otherStorage = targetStorage === localStorageRef ? sessionStorageRef : localStorageRef;

  if (accessToken) targetStorage.setItem('accessToken', accessToken);
  if (refreshToken) targetStorage.setItem('refreshToken', refreshToken);

  if (accessToken) otherStorage.removeItem('accessToken');
  if (refreshToken) otherStorage.removeItem('refreshToken');
};

export const setAuthMetaValue = (key, value) => {
  if (!hasWindow()) return;
  const localStorageRef = getLocalStorage();
  const sessionStorageRef = getSessionStorage();
  const targetStorage = getActiveStorage();
  const otherStorage = targetStorage === localStorageRef ? sessionStorageRef : localStorageRef;

  if (value === null || value === undefined || value === '') {
    targetStorage.removeItem(key);
    otherStorage.removeItem(key);
    return;
  }

  targetStorage.setItem(key, value);
  otherStorage.removeItem(key);
};

export const clearAuthSession = () => {
  if (!hasWindow()) return;
  const localStorageRef = getLocalStorage();
  const sessionStorageRef = getSessionStorage();
  [...AUTH_KEYS, ...AUTH_META_KEYS].forEach((key) => {
    localStorageRef.removeItem(key);
    sessionStorageRef.removeItem(key);
  });
};
